/* eslint-disable @typescript-eslint/naming-convention */
import { loadStripe } from '@stripe/stripe-js/pure' // Don't import loadStripe from @stripe/stripe-js. It doesn't throw error when there is an error (tested with the network error)
import { Stripe, PaymentMethod, PaymentIntent } from '@stripe/stripe-js'
import { getCurrentVisitUrlParams, getCurrentVisitUrlParamsString, getSanitizedMarketingSearchParamsString, getFbp, getFbc, generateAndStoreMetaPurchaseEventId, getLocalId } from 'Lib/AppUtils'
import * as Sentry from '@sentry/react'
import { Store } from 'redux'
import { ActionType } from 'Reducers'
import { axios } from 'Fetcher'
import i18n from 'i18n'
import { RouteNames } from 'RouteNames'
import { firebaseServicePromise } from 'store'
import { getPaymentUrl } from 'Lib/EventsUtils'
import { getPlatform, getUserLanguage } from 'Lib'
import { isMorCheckoutEnabled } from 'Lib/StripeUtils'

let stripe: Stripe | null = null

export async function initStripeService(store: Store): Promise<Stripe | null> {
  const STRIPE_KEY = __DEV__
    ? 'pk_test_51HqeBSKNaJOrhJwVsmX16nd9oBNWw23DDi4s17G6CK0uzb9yORAOgNrhFwm9wZIrmKX4Z3NZuvmsCxyaRag15Wte00hBL8dofF'
    : 'pk_live_51HqeBSKNaJOrhJwVxSAzrBmJIQJN7SZKvjD0q7dNZB7uNWQsNsyRpxBkiKMAzU7slJDRYO9nGTDRD5ynAWn5WiIj00cjndDKQj'

  if (!stripe) {
    try {
      stripe = await loadStripe(STRIPE_KEY)

      if (!stripe) {
        throw new Error('Stripe is not initialized')
      }
    } catch (error) {
      logEvent('web_stripeLoadScriptFail', error)
      Sentry.captureException(error)

      // eslint-disable-next-line
      await new Promise((r) => setTimeout(r, 1000));
      return await initStripeService(store)
    }
  }

  store.dispatch({ type: ActionType.STRIPE_INITIALIZED })

  return stripe
}

export interface StripePricesType {
  prices: IStripePrice[]
  isEU: boolean
  countryCode?: string
  region?: string
  currency: string
  paypalElementsEnabled: boolean
  paypalCheckoutEnabled: boolean
  paypalNativeFallbackEnabled: boolean
  isPurchaseAvailable: boolean
  morCheckoutEnabled?: boolean
}
interface FetchStripePricesResponse {
  data: StripePricesType
}

export const fetchStripePrices = async (promo: SubscriptionGroupId, currency?: string): Promise<FetchStripePricesResponse> => {
  let url = `/api/payment/getProducts/${promo}`

  if (currency) {
    url += `?currency=${currency}`
  }

  const response = await axios({
    method: 'GET',
    url
  })

  return { data: response.data.data }
}

export const extractStripeMetadataFromPrice = (price: IStripePrice): { group: SubscriptionGroupId, monthDuration: number } => {
  const group = price.metadata.group
  const monthDuration = price.recurring.interval === 'year' ? price.recurring.interval_count * 12 : price.recurring.interval_count
  return { group, monthDuration }
}

// still required for external windows (e.g. paypal mobile) return URL. the return URL is appended with the success/error status and the user needs to be redirected from there
export const getStripeElementsReturnUrl = (subscriptionId: string, selecteedPackageId: string) => {
  const baseUrl = location.hostname === 'localhost' ? 'http://localhost:3000' : `https://${location.hostname}`
  const searchParamsString = getSanitizedMarketingSearchParamsString().replace('?', '&')

  const paymentPath = getPaymentUrl()
  return `${baseUrl}${paymentPath}?send_event=true&subscriptionId=${subscriptionId}&selectedPackageId=${selecteedPackageId}${searchParamsString}`
}

export const createStripeSubscriptionHandler = async (
  priceId: string,
  email: string,
  lang: string,
  trialId?: string,
  withDiscount?: boolean,
  gender?: IGender | null
): Promise<{ type: 'payment' | 'setup', clientSecret: string, customerId: string, subscriptionId: string }> => {
  const url = '/api/payment/createSubscription'
  const firebase = await firebaseServicePromise

  let searchParamsString = getSanitizedMarketingSearchParamsString().replace('?', '')

  // Remove token param from search params so we don't send it twice in case we
  // already have it in the query params
  if (searchParamsString) {
    const paramsArray = searchParamsString.split('&')
    const filteredParamsArray = paramsArray.filter(param => !param.startsWith('token='))

    searchParamsString = filteredParamsArray.join('&')
  }

  const firstVisit = localStorage.getItem('firstVisit')
  const platform = getPlatform()

  const idToken = await firebase.getIdToken()

  const response = await axios({
    method: 'POST',
    url,
    data: {
      priceId,
      trialId,
      origin,
      urlParams: searchParamsString,
      lang,
      email,
      withDiscount,
      firstVisit,
      gender,
      platform,
      // Meta CAPI attribution signals — persisted via Stripe metadata so the
      // server-side Purchase CAPI event can attribute the conversion to the
      // originating ad click. The server also captures IP + User-Agent from
      // the HTTP request itself, so no need to send those here.
      fbp: getFbp(),
      fbc: getFbc(),
      // Shared dedup id between browser Pixel and server CAPI. Generated
      // here, stashed in sessionStorage, read back by the saga that fires
      // the browser-side Purchase on the payment-success page.
      metaEventId: generateAndStoreMetaPurchaseEventId(),
      localId: getLocalId()
    },
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  })

  return response.data.data
}

export const purchaseEbookHandler = async (
  priceId: string,
  currency: string,
  withDiscount: boolean = true,
  successEndpoint: string = RouteNames.EBOOK_PURCHASE_SUCCESS,
  cancelEndpoint: string = RouteNames.EBOOK_PURCHASE
): Promise<{ paymentIntent?: PaymentIntent, url?: string }> => {
  const url = '/api/payment/purchaseEbook'
  const firebase = await firebaseServicePromise

  const idToken = await firebase.getIdToken()
  const response = await axios({
    method: 'POST',
    url,
    data: {
      priceId,
      currency,
      withDiscount,
      // The MoR checkout is hosted by Stripe, which otherwise renders it in the
      // browser/device language rather than the one the user onboarded in.
      lang: getUserLanguage(),
      // Where the MoR checkout returns to. Only the client knows the buyer's flow.
      successEndpoint,
      cancelEndpoint,
      // Carried into the MoR checkout's return URLs so marketing params survive the
      // redirect, same as the subscription flow.
      urlParams: getCurrentVisitUrlParamsString().replace('?', '')
    },
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  })

  return response.data.data
}

// checkoutSessionId stands in for purchaseId on the MoR ebook return, where the browser
// only ever sees the session id — the server resolves the PaymentIntent from it.
export const saveSourceMetadata = async (purchaseId: string, purchaseType: PurchaseType, checkoutSessionId?: string) => {
  const url = '/api/payment/saveSourceMetadata'
  const firebase = await firebaseServicePromise

  const idToken = await firebase.getIdToken()

  let searchParamsString = getSanitizedMarketingSearchParamsString().replace('?', '')

  // Remove token param from search params so we don't send it twice in case we
  // already have it in the query params
  if (searchParamsString) {
    const paramsArray = searchParamsString.split('&')
    const filteredParamsArray = paramsArray.filter(param => !param.startsWith('token='))

    searchParamsString = filteredParamsArray.join('&')
  }

  const response = await axios({
    method: 'POST',
    url,
    data: {
      purchaseId,
      checkoutSessionId,
      urlParams: searchParamsString,
      purchaseType
    },
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  })

  return response.data.data
}

export const updateSubscription = async (priceId: string, withDiscount: boolean, lang: string): Promise<{ subscription: IStripeSubscription }> => {
  const url = '/api/payment/updateSubscription'

  const firebase = await firebaseServicePromise

  let idToken: string
  try {
    idToken = await firebase.getIdToken()
  } catch (e) {
    throw new Error('Error getting firebase token id: You are not logged in!')
  }

  const response = await axios({
    method: 'POST',
    url,
    data: {
      priceId,
      withDiscount,
      lang
    },
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  })

  return response.data.data
}

export const updateSubscriptionWithSpecialOfferHandler = async (specialOfferDuration: SpecialOfferDuration): Promise<{}> => {
  const url = '/api/payment/updateSubscriptionWithSpecialOffer'

  const firebase = await firebaseServicePromise

  let idToken: string
  try {
    idToken = await firebase.getIdToken()
  } catch (e) {
    throw new Error('Error getting firebase token id: You are not logged in!')
  }

  try {
    const response = await axios({
      method: 'POST',
      url,
      data: {
        duration: specialOfferDuration
      },
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    })

    return response.data.data
  } catch (e) {
    Sentry.captureException(e)
    throw new Error('Error creating payment session')
  }
}

export const cancelStripeSubscriptionHandler = async (): Promise<{}> => {
  const url = '/api/payment/cancelSubscription'
  const firebase = await firebaseServicePromise

  let idToken: string
  try {
    idToken = await firebase.getIdToken()
  } catch (e) {
    throw new Error('Error getting firebase token id: You are not logged in!')
  }

  try {
    const response = await axios({
      method: 'POST',
      url,
      data: {
      },
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    })

    return response.data.data
  } catch (e) {
    Sentry.captureException(e)
    throw new Error('Error creating payment session')
  }
}

export const restoreStripeSubscriptionHandler = async (): Promise<{}> => {
  const url = '/api/payment/restoreSubscription'
  const firebase = await firebaseServicePromise

  let idToken: string
  try {
    idToken = await firebase.getIdToken()
  } catch (e) {
    throw new Error('Error getting firebase token id: You are not logged in!')
  }

  try {
    const response = await axios({
      method: 'POST',
      url,
      data: {
      },
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    })

    return response.data.data
  } catch (e) {
    Sentry.captureException(e)
    throw new Error('Error creating payment session')
  }
}

export const pauseStripeSubscriptionHandler = async (months: number): Promise<{}> => {
  const url = '/api/payment/pauseSubscription'
  const firebase = await firebaseServicePromise

  let idToken: string
  try {
    idToken = await firebase.getIdToken()
  } catch (e) {
    throw new Error('Error getting firebase token id: You are not logged in!')
  }

  try {
    const response = await axios({
      method: 'POST',
      url,
      data: {
        months
      },
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    })

    return response.data.data
  } catch (e) {
    Sentry.captureException(e)
    throw new Error('Error creating payment session')
  }
}

export const unpauseStripeSubscriptionHandler = async (): Promise<{}> => {
  const url = '/api/payment/unpauseSubscription'
  const firebase = await firebaseServicePromise

  let idToken: string
  try {
    idToken = await firebase.getIdToken()
  } catch (e) {
    throw new Error('Error getting firebase token id: You are not logged in!')
  }

  try {
    const response = await axios({
      method: 'POST',
      url,
      data: {
      },
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    })

    return response.data.data
  } catch (e) {
    Sentry.captureException(e)
    throw new Error('Error creating payment session')
  }
}

export const createStripeCheckoutSession = async (priceId: string, lang: string, trialId?: string, withDiscount?: boolean, gender?: IGender | null, successEndpoint: string = RouteNames.PAYMENT_SUCCESS, cancelEndpoint: string = RouteNames.PAYMENT): Promise<{ sessionId: string, url?: string }> => {
  const url = '/api/payment/createCheckoutSession'
  const baseUrl = location.hostname === 'localhost' ? 'http://localhost:3000' : `https://${location.hostname}`
  const firstVisit = localStorage.getItem('firstVisit')
  const platform = getPlatform()

  const successUrlObject = new URL(`${baseUrl}${successEndpoint}`)
  const cancelUrlObject = new URL(`${baseUrl}${cancelEndpoint}`)

  // Marketing attribution params only — auth on the return page comes from the
  // persisted Firebase session, never from the URL (JWTs in URLs leak to analytics).
  getCurrentVisitUrlParams().forEach((value, key) => {
    successUrlObject.searchParams.set(key, value)
    cancelUrlObject.searchParams.set(key, value)
  })
  successUrlObject.searchParams.set('send_event', 'true')

  // Appended raw: Stripe replaces this placeholder on redirect, but only while the
  // braces stay unencoded — searchParams.set would percent-encode them.
  const successUrl = `${successUrlObject.toString()}&session_id={CHECKOUT_SESSION_ID}`
  const cancelUrl = cancelUrlObject.toString()

  const firebase = await firebaseServicePromise

  let idToken: string
  try {
    idToken = await firebase.getIdToken()
  } catch (e) {
    throw new Error('Error getting firebase token id: You are not logged in!')
  }

  const response = await axios({
    method: 'POST',
    url,
    data: {
      priceId,
      trialId,
      origin,
      successUrl,
      cancelUrl,
      lang,
      withDiscount,
      firstVisit,
      gender,
      platform,
      fbp: getFbp(),
      fbc: getFbc(),
      metaEventId: generateAndStoreMetaPurchaseEventId(),
      localId: getLocalId(),
      // Declares the MoR routing getProducts gave this visitor — the server
      // fails the request rather than silently creating a non-MoR (untaxed)
      // session when it can't re-confirm a MoR country at session time.
      morCheckout: isMorCheckoutEnabled()
    },
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  })

  const sessionId = response.data.data.sessionId

  if (!sessionId) {
    throw new Error('Error creating Stripe checkout session. Session id is missing!')
  }
  return response.data.data
}

export const setUpStripeCardCustomer = async (
  paymentMethod: PaymentMethod,
  pricingId: string
): Promise<any> => {
  // Add Stripe Subscription type later? if necessary
  const firebaseService = await firebaseServicePromise
  const idToken = await firebaseService.getIdToken()
  try {
    const response = await axios({
      method: 'POST',
      url: '/api/payment/cardSubscribe',
      data: {
        pricingId,
        paymentMethodId: paymentMethod.id,
        locale: i18n.language
      },
      headers: {
        Authorization: `Bearer ${idToken}`
      }
    })

    return { subscription: response.data.data.subscription }
  } catch (e) {
    return { error: e.response.data.error.code }
  }
}

export async function getCustomerPortalUrl () {
  const firebaseService = await firebaseServicePromise
  const idToken = await firebaseService.getIdToken()

  const response: any = await axios({
    method: 'GET',
    url: '/api/payment/getCustomerPortalUrl',
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  })

  if (response.status !== 200) {
    const error = new Error('Could not fetch customer portal url')
    logEvent('web_stripePortalUrlError', error)
    throw error
  }

  if (!response.data.ok) {
    throw new Error('Could not fetch customer portal url')
  }

  return response.data.data
  // Linking.openURL(resJson.data)
}

export async function goToCustomerPortal () {
  const url = await getCustomerPortalUrl()
  window.open(url, '_blank')
}
